import type { Facture, FactureLigne } from '@shared/types/Facture';
import type { Ecriture } from '@shared/types/Ecriture';
import { roundFCFA } from '@shared/utils/round';
import { uuid } from '@shared/utils/uuid';

/**
 * Service de calcul et de comptabilisation d'une facture de vente.
 * Génère les écritures : Débit 411 / Crédit 70x + 4431 (TVA collectée).
 */
export class FactureVenteService {
  static calculer(lignes: Omit<FactureLigne, 'montant_ht' | 'montant_tva' | 'montant_ttc'>[]): {
    lignes: FactureLigne[];
    total_ht: number;
    total_tva: number;
    total_ttc: number;
  } {
    const calc: FactureLigne[] = lignes.map((l) => {
      const montant_ht = roundFCFA(l.quantite * l.prix_unitaire);
      const montant_tva = roundFCFA(montant_ht * l.taux_tva);
      return { ...l, montant_ht, montant_tva, montant_ttc: montant_ht + montant_tva };
    });
    const total_ht = calc.reduce((s, l) => s + l.montant_ht, 0);
    const total_tva = calc.reduce((s, l) => s + l.montant_tva, 0);
    return { lignes: calc, total_ht, total_tva, total_ttc: total_ht + total_tva };
  }

  static genererEcritures(facture: Facture, client_compte: string): Ecriture[] {
    const ecritures: Ecriture[] = [];
    const dateEcr = this.formatDate(facture.date);
    const periode = dateEcr.slice(6, 10) + '-' + dateEcr.slice(3, 5);
    const ref = facture.numero;

    // Débit client (total TTC)
    ecritures.push(
      this.ecr(
        facture.tenant_id,
        dateEcr,
        periode,
        client_compte,
        facture.total_ttc,
        0,
        `Facture ${ref}`,
        ref,
        facture.id,
      ),
    );
    // Crédit comptes de produits par ligne
    for (const ligne of facture.lignes) {
      ecritures.push(
        this.ecr(
          facture.tenant_id,
          dateEcr,
          periode,
          ligne.compte_produit,
          0,
          ligne.montant_ht,
          `Vente ${ref}`,
          ref,
          facture.id,
          ligne.axe_cdc,
        ),
      );
    }
    // Crédit TVA collectée
    if (facture.total_tva > 0) {
      ecritures.push(
        this.ecr(
          facture.tenant_id,
          dateEcr,
          periode,
          '4431',
          0,
          facture.total_tva,
          `TVA col. ${ref}`,
          ref,
          facture.id,
        ),
      );
    }
    return ecritures;
  }

  private static ecr(
    tenant_id: string,
    date: string,
    periode: string,
    compte: string,
    debit: number,
    credit: number,
    libelle: string,
    ref: string,
    factureId?: string,
    axe?: string,
  ): Ecriture {
    const e: Ecriture = {
      id: uuid('ecr'),
      tenant_id,
      date,
      periode,
      journal: 'VE',
      compte,
      libelle_compte: '',
      libelle,
      debit,
      credit,
      source: 'facture' as const,
      numero_piece: ref,
      facture_id: factureId,
      genere_le: new Date().toISOString(),
    };
    if (axe) e.axe_cdc = axe;
    return e;
  }

  private static formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
}
